import { useState, useEffect, useRef } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isViewer, setIsViewer] = useState(false);
  const [canWrite, setCanWrite] = useState(false);
  const [loading, setLoading] = useState(true);
  const [needsTwoFactor, setNeedsTwoFactor] = useState(false);
  const [checkingTwoFactor, setCheckingTwoFactor] = useState(false);
  const [twoFactorVerified, setTwoFactorVerified] = useState(false);
  const twoFactorCheckInProgress = useRef(false);

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          setTimeout(() => {
            checkAdminStatus(session.user.id);
            checkTwoFactorStatus(session.user);
          }, 0);
        } else {
          setIsAdmin(false);
          setLoading(false);
          setNeedsTwoFactor(false);
          setTwoFactorVerified(false);
          // Clear 2FA verification on sign out
          if (event === 'SIGNED_OUT') {
            // Remove 2FA verification from localStorage
            Object.keys(localStorage).forEach(key => {
              if (key.startsWith('2fa_verified_')) {
                localStorage.removeItem(key);
              }
            });
          }
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        checkAdminStatus(session.user.id);
        checkTwoFactorStatus(session.user);
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkAdminStatus = async (userId: string) => {
    try {
      // Utiliser les fonctions RPC sécurisées plutôt que de lire directement user_roles (évite les problèmes de RLS)
      const [{ data: isAdminFn }, { data: isViewerFn }] = await Promise.all([
        supabase.rpc('is_admin'),
        supabase.rpc('is_viewer'),
      ]);

      const isAdminUser = Boolean(isAdminFn);
      const isViewerUser = Boolean(isViewerFn);

      setIsAdmin(isAdminUser);
      setIsViewer(isViewerUser);
      setCanWrite(isAdminUser); // Seuls les admins peuvent écrire
    } catch (error) {
      console.error("Error checking user roles:", error);
      setIsAdmin(false);
      setIsViewer(false);
      setCanWrite(false);
    } finally {
      setLoading(false);
    }
  };

  const checkTwoFactorStatus = async (currentUser: User) => {
    // Prevent multiple simultaneous checks
    if (twoFactorCheckInProgress.current) {
      return;
    }

    // If this user has already successfully passed 2FA in this session,
    // don't trigger any remote calls or UI blocking again.
    const localVerified = localStorage.getItem(`2fa_verified_${currentUser.id}`) === 'true';
    if (localVerified) {
      setTwoFactorVerified(true);
      setNeedsTwoFactor(false);
      // Make sure flags are clean so the layout doesn't flash/reload
      setCheckingTwoFactor(false);
      twoFactorCheckInProgress.current = false;
      return;
    }
    
    twoFactorCheckInProgress.current = true;
    setCheckingTwoFactor(true);
    try {
      // Always verify with server for security on first login
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        setNeedsTwoFactor(false);
        setCheckingTwoFactor(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke('verify-2fa-session', {
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
        }
      });

      if (error) {
        console.error('Error checking 2FA status:', error);
        
        // If token is invalid (401), sign out the user
        if (error.message?.includes('Invalid token') || error.message?.includes('401')) {
          console.log('[AUTH] Invalid token detected, signing out');
          await supabase.auth.signOut();
          setNeedsTwoFactor(false);
          setCheckingTwoFactor(false);
          return;
        }
        
        // Fallback to database check for other errors
        const { data: dbData, error: dbError } = await supabase
          .from('user_2fa')
          .select('enabled')
          .eq('user_id', currentUser.id)
          .maybeSingle();

        if (dbError) {
          console.error('Error checking 2FA status from DB:', dbError);
          setNeedsTwoFactor(false);
          setCheckingTwoFactor(false);
          return;
        }

        const enabled = dbData?.enabled || false;
        setNeedsTwoFactor(enabled);
        setTwoFactorVerified(false);
      } else {
        const needsTwoFactor = data?.needsTwoFactor || false;
        setNeedsTwoFactor(needsTwoFactor);
        setTwoFactorVerified(false);
      }
    } catch (error) {
      console.error('Error in checkTwoFactorStatus:', error);
      setNeedsTwoFactor(false);
    } finally {
      setCheckingTwoFactor(false);
      twoFactorCheckInProgress.current = false;
    }
  };
  const markTwoFactorVerified = () => {
    if (user) {
      localStorage.setItem(`2fa_verified_${user.id}`, 'true');
      setTwoFactorVerified(true);
      setNeedsTwoFactor(false);
    }
  };

  const clearTwoFactorVerification = () => {
    if (user) {
      localStorage.removeItem(`2fa_verified_${user.id}`);
      setTwoFactorVerified(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    // Get client IP (using a public API)
    let clientIp = 'unknown';
    try {
      const ipResponse = await fetch('https://api.ipify.org?format=json');
      const ipData = await ipResponse.json();
      clientIp = ipData.ip;
    } catch (err) {
      console.error('Failed to get client IP:', err);
    }

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    // Track authentication attempt (success or failure) - without password for security
    try {
      await supabase.functions.invoke('track-auth-attempt', {
        body: {
          email,
          success: !error,
          ip: clientIp
        }
      });
    } catch (err) {
      console.error('Failed to track auth attempt:', err);
    }
    
    // Track IP after successful login (existing functionality)
    if (!error) {
      try {
        await supabase.functions.invoke('track-auth-ip');
      } catch (err) {
        console.error('Failed to track auth IP:', err);
      }
    }
    
    return { error };
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
        },
      },
    });
    return { error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  return {
    user,
    session,
    isAdmin,
    isViewer,
    canWrite,
    canAccessDashboard: isAdmin || isViewer,
    loading,
    needsTwoFactor,
    checkingTwoFactor,
    twoFactorVerified,
    markTwoFactorVerified,
    clearTwoFactorVerification,
    signIn,
    signUp,
    signOut,
  };
};
